import { ChangeEvent, FC, useEffect, useReducer, useState } from 'react';
import { Container, CommunicationSettings, SubTitle } from './Settings.styles';
import { Button, Dropdown, Message, Modal, Section, Switch } from '../../../../components';
import { SectionTitle, Separator } from '../../MyAccount.styles';
import { Delete, SettingsOutlined } from '@mui/icons-material';
import { Option } from '../../../../components/dropdown/Dropdown';
import {
	communicationPreferenceInitialState,
	communicationPreferencesReducer,
} from './forms/communicationPreferences.form';
import { deleteAccountInitialState, deleteAccountReducer } from './forms/deleteAccount.form';
import { useToastStore, useUserStore } from '../../../../stores';
import { handleLogout, postUserDeleteAccount, postUserNotifications } from '../../../../services';

const deleteReasons: Option[] = [
	{ value: 'system_unused', label: 'Não faço uso do sistema' },
	{
		value: 'offers_not_related_to_business',
		label: 'Os anúncios não são relacionados ao meu negócio',
	},
	{ value: 'system_issues', label: 'Tive problemas ou erros com a plataforma' },
	{ value: 'other', label: 'Outro' },
];

const Settings: FC = () => {
	const { user, setUser } = useUserStore();
	const { setToast } = useToastStore();
	const [openDeleteAccountModal, setOpenDeleteAccountModal] = useState(false);

	const [communicationState, communicationDispatch] = useReducer(
		communicationPreferencesReducer,
		communicationPreferenceInitialState
	);
	const [deleteAccountState, deleteAccountDispatch] = useReducer(
		deleteAccountReducer,
		deleteAccountInitialState
	);

	const handleToggleComunicationPreferences = (e: ChangeEvent<HTMLInputElement>) => {
		const { name } = e.target;
		communicationDispatch({ type: 'TOGGLE_CONTACT', field: name });
	};

	const handleDeleteAccountModal = () => {
		setOpenDeleteAccountModal(true);
	};

	const handleDropdownChange = (option: Option) => {
		deleteAccountDispatch({ type: 'SET_SELECTED_OPTION', option });
	};

	const handleDescriptionChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
		const { value } = e.target;
		deleteAccountDispatch({ type: 'SET_DESCRIPTION_VALUE', value });
	};

	const handleSubmitSettings = async () => {
		const body = {
			idUsuario: user!.id,
			tagReceberEmail: communicationState.receiveEmail ? '1' : '0',
			tagReceberPush: communicationState.receivePhoneMessages ? '1' : '0',
		};

		try {
			await postUserNotifications(body);
			setUser({
				...user!,
				tagReceberEmail: body.tagReceberEmail,
				tagReceberPush: body.tagReceberPush,
			});
			setToast({ message: 'Configurações atualizadas com sucesso!', variant: 'success' });
		} catch {
			setToast({ message: 'Erro ao atualizar as configurações!', variant: 'error' });
		}
	};

	const handleDeleteAccount = async () => {
		const body = {
			idUsuario: user!.id,
			motivoEncerramento: deleteAccountState.selectedReason.label,
		};

		try {
			await postUserDeleteAccount(body);
			handleLogout();
		} catch {
			setToast({
				message: 'Erro ao apagar a conta! Contate o administrador do sistema.',
				variant: 'error',
			});
		}
	};

	useEffect(() => {
		deleteAccountDispatch({
			type: 'SET_SELECTED_OPTION',
			option: deleteAccountInitialState.selectedReason,
		});
		deleteAccountDispatch({
			type: 'SET_DESCRIPTION_VALUE',
			value: deleteAccountInitialState.description,
		});
	}, [openDeleteAccountModal]);

	useEffect(() => {
		communicationDispatch({
			type: 'REPLACE_STATE',
			state: {
				receiveEmail: user!.tagReceberEmail === '1',
				receivePhoneMessages: user!.tagReceberPush === '1',
			},
		});
	}, [user]);

	return (
		<Container>
			<Section>
				<SectionTitle>Configurações</SectionTitle>
				<Separator />
				<CommunicationSettings>
					<SubTitle>Comunicação</SubTitle>
					<Switch
						label='Receber emails'
						checked={communicationState.receiveEmail}
						onChange={handleToggleComunicationPreferences}
						name='receiveEmail'
					/>
					<Switch
						label='Receber notificações no celular'
						checked={communicationState.receivePhoneMessages}
						onChange={handleToggleComunicationPreferences}
						name='receivePhoneMessages'
					/>
					<Button variant='contained' fitContent onClick={handleSubmitSettings}>
						<SettingsOutlined fontSize='inherit' />
						<span>Atualizar</span>
					</Button>
				</CommunicationSettings>
			</Section>
			<Section>
				<Button variant='text' onClick={handleDeleteAccountModal}>
					<Delete fontSize='inherit' />
					<span>Apagar conta</span>
				</Button>
			</Section>
			<Modal
				open={openDeleteAccountModal}
				onClose={() => setOpenDeleteAccountModal(false)}
				title='Deseja mesmo apagar sua conta?'
				primaryButtonText='Apagar'
				secondaryButtonText='Cancelar'
				onPrimaryButtonClick={handleDeleteAccount}
				onSecondaryButtonClick={() => setOpenDeleteAccountModal(false)}
			>
				<div>
					<Dropdown
						options={deleteReasons}
						selected={deleteAccountState.selectedReason}
						onSelect={handleDropdownChange}
						placeholder='Qual o motivo?'
					/>
					<Message
						value={deleteAccountState.description}
						onChange={handleDescriptionChange}
						placeholder='Descreva o motivo'
					/>
				</div>
			</Modal>
		</Container>
	);
};

export default Settings;
